/**
 * Smart Shikka AI Core — Adopted Capability Wiring Registry
 * PR80: Adopted Capability Wiring Registry — No Execution
 *
 * Wires adopted Kami/Waza/Aiko modules into Smart Shikka capability metadata.
 * No runtime execution. No provider calls. No network. No DB.
 */
const BLOCKED_ACTIONS = [
    { action: "execute_kami_renderer", reason: "No Kami execution in PR80" },
    { action: "execute_waza_skill", reason: "No Waza skill execution in PR80" },
    { action: "inject_aiko_icon_runtime", reason: "No Aiko runtime injection in PR80" },
    { action: "render_document", reason: "No document rendering in PR80" },
    { action: "run_workflow", reason: "No workflow execution in PR80" },
    { action: "run_agent", reason: "No agent execution in PR80" },
    { action: "provider_call", reason: "No provider calls in PR80" },
    { action: "network_call", reason: "No network calls in PR80" },
    { action: "db_write", reason: "No database writes in PR80" },
    { action: "supabase_call", reason: "No Supabase calls in PR80" },
    { action: "secret_read", reason: "No secret access in PR80" },
    { action: "browser_action", reason: "No browser automation in PR80" },
    { action: "desktop_action", reason: "No desktop automation in PR80" },
    { action: "file_read", reason: "No file reads in PR80" },
    { action: "file_write", reason: "No file writes in PR80" },
    { action: "child_process", reason: "No child_process in PR80" },
    { action: "shell_command", reason: "No shell commands in PR80" },
    { action: "auto_save", reason: "No auto-save in PR80" },
    { action: "auto_delete", reason: "No auto-delete in PR80" },
    { action: "payment_trigger", reason: "No payment triggers in PR80" },
    { action: "role_mutation", reason: "No role mutations in PR80" },
    { action: "secret_mutation", reason: "No secret mutations in PR80" },
];
function deepFreeze(obj) {
    Object.freeze(obj);
    for (const value of Object.values(obj)) {
        if (value && typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return obj;
}
const capabilities = [
    {
        id: "document_rendering_kami",
        displayName: "Smart Shikka AI Document Rendering Capability, adapted from Kami",
        sourceAdoptionId: "kami_document_renderer",
        sourceModule: "packages/core/src/kami-document-renderer",
        capabilityType: "document_rendering",
        smartShikkaUseCase: "Document template preview and render-plan metadata for Smart Shikka AI",
        assistantFutureUse: "Smart Shikka Assistant document task — Kami capability",
        allowedInPr80: true,
        executionAllowedInPr80: false,
        runtimeIntegrationAllowedInPr80: false,
        requiresOwnerApproval: true,
        requiresUserApproval: true,
        blockedActions: [...BLOCKED_ACTIONS],
        dependsOnModules: [
            "packages/core/src/kami-document-renderer",
            "packages/core/src/source-adoption",
        ],
        futurePrScope: [
            "PR81: Kami document renderer preview — no live render execution",
            "Future: Smart Shikka Assistant document task integration",
        ],
        notes: [
            "Kami source: https://github.com/tw93/Kami, commit 594bfb8f2c62f26102a377c2e6cdb8cafa7d61f1, MIT license",
            "7 templates adopted: changelog, letter, long-doc, one-pager, portfolio, resume, slides-weasy",
            "No fonts, demos, showcase, or scripts copied",
        ],
    },
    {
        id: "workflow_skill_planning_waza",
        displayName: "Smart Shikka AI Workflow Skill Planning Capability, adapted from Waza",
        sourceAdoptionId: "waza_workflow_skills",
        sourceModule: "packages/core/src/waza-workflow-skills",
        capabilityType: "workflow_skill_planning",
        smartShikkaUseCase: "Workflow skill planning and preview metadata for Smart Shikka AI",
        assistantFutureUse: "Smart Shikka Assistant planning/check task — Waza capability",
        allowedInPr80: true,
        executionAllowedInPr80: false,
        runtimeIntegrationAllowedInPr80: false,
        requiresOwnerApproval: true,
        requiresUserApproval: true,
        blockedActions: [...BLOCKED_ACTIONS],
        dependsOnModules: [
            "packages/core/src/waza-workflow-skills",
            "packages/core/src/source-adoption",
        ],
        futurePrScope: [
            "PR82: Waza workflow planner preview — no skill execution",
            "Future: Smart Shikka Assistant planning/check task integration",
        ],
        notes: [
            "Waza source: https://github.com/tw93/Waza, commit ac1cc9d685718996df081d70e677d527c3ff981f, MIT license",
            "5 skills adopted: think, check, hunt, read, health",
            "No plugins, tests, scripts, or fetch scripts copied",
        ],
    },
    {
        id: "icon_system_aiko",
        displayName: "Smart Shikka AI Icon System Capability, adapted from Aiko",
        sourceAdoptionId: "aiko_icon_system",
        sourceModule: "packages/core/src/aiko-icon-system",
        capabilityType: "icon_system",
        smartShikkaUseCase: "Icon system preview and UI identity metadata for Smart Shikka AI",
        assistantFutureUse: "Smart Shikka Assistant UI identity/icon task — Aiko capability",
        allowedInPr80: true,
        executionAllowedInPr80: false,
        runtimeIntegrationAllowedInPr80: false,
        requiresOwnerApproval: true,
        requiresUserApproval: true,
        blockedActions: [...BLOCKED_ACTIONS],
        dependsOnModules: [
            "packages/core/src/aiko-icon-system",
            "packages/core/src/source-adoption",
        ],
        futurePrScope: [
            "PR83: Aiko icon registry/UI preview — no runtime injection",
            "Future: Smart Shikka Assistant UI identity/icon task integration",
        ],
        notes: [
            "Aiko source: https://github.com/tw93/aiko, commit b7ba4c7ddff6416760f0cdf5b53d6cdd65fd1a12, MIT license (in README)",
            "icons.svg adopted",
            "No index.html or preview.png copied",
        ],
    },
];
const registry = deepFreeze({
    capabilities,
});
export function getAdoptedCapabilities() {
    return registry.capabilities;
}
export function getAdoptedCapabilityById(id) {
    return registry.capabilities.find((c) => c.id === id);
}
export { registry as adoptedCapabilityWiringRegistry };
//# sourceMappingURL=adoptedCapabilityWiringRegistry.js.map